import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  fetchPackages,
  fetchCategories,
  fetchSubCategoriesForList,
  deletePackage,
} from "../../components/Manual_List/fetch/slices";
import { RootState } from "../../components/Manual_List/fetch/store";
import { Pencil, Trash2 } from "lucide-react";
import { Package, Category, SubCategory, Language } from "../../Types/types";
import { addpackagetranslation } from "./addpackagetranslation";
import { useLanguage } from "../../components/LanguageContext";
import { useAppDispatch } from "../../components/Manual_List/fetch/hook";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CustomPagination from "../../components/Manual_List/Pagination";
import ItemsPerPageSelect from "../../components/Manual_List/ItemsPerPage";
import { SelectChangeEvent } from "@mui/material/Select";
import translationManualList from "../../components/Manual_List/translationManualList";
import SearchIcon from "@mui/icons-material/Search";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { ConfirmationModal } from "../../components/shared/ConfrimationModal";
import CircularProgress from "@mui/material/CircularProgress";
import { getLabel } from "../../utils/getLabel";
import { AddPackageModal } from "../../components/shared/AddPackageModal";
import { GeneralTranslation } from "../../Translations/GeneralTranslation";

const PackagesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const packages: Package[] = useSelector(
    (state: RootState) => state.data.packages.data
  );
  const categories: Category[] = useSelector(
    (state: RootState) => state.data.categories.data
  );
  const subCategories: SubCategory[] = useSelector(
    (state: RootState) => state.data.fetchSubCategoriesForListStatus.data
  );
  const totalPackages: number = useSelector(
    (state: RootState) => state.data.totalPackages
  );

  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState<Package | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAddPackageModalOpen, setIsAddPackageModalOpen] = useState(false);
  const [values, setValues] = useState({});

  const { language } = useLanguage();
  const t = addpackagetranslation[language as Language];
  const ml = translationManualList[language as Language];
  const gt = GeneralTranslation[language as Language];

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));


  useEffect(() => {
    dispatch(fetchCategories(language));
  }, [dispatch, language]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      await dispatch(
        fetchPackages({
          language,
          categoryIds: categoryFilter ? [categoryFilter] : [],
          subCategoryIds: subcategoryFilter ? [subcategoryFilter] : [],
          page: page - 1,
          count: itemsPerPage,
          searchQuery: searchTerm,
        })
      );
      setLoading(false);
    }
    fetchData();
  }, [
    dispatch,
    language,
    page,
    itemsPerPage,
    categoryFilter,
    subcategoryFilter,
    searchTerm,
  ]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleCategoryChange = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const categoryId = event.target.value;
    setCategoryFilter(categoryId);
    setSubcategoryFilter("");
    setPage(1);

    if (categoryId) {
      await dispatch(
        fetchSubCategoriesForList({ language, categoryId: Number(categoryId) })
      );
    }
  };

  const handleSubcategoryChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSubcategoryFilter(event.target.value);
    setPage(1);
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(event.target.value as number);
    setPage(1);
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleDeletePackage = (id: number) => {
    dispatch(deletePackage(id))
      .then((response: any) => {
        if (response.error) {
          toast.error(gt.deleteFailed);
        } else {
          toast.success(gt.deleteSuccess);
        }
      })
      .finally(() => {
        setIsConfirmationModalOpen(false);
      });
  };

  const openConfirmationModal = (pkg: Package) => {
    setPackageToDelete(pkg);
    setIsConfirmationModalOpen(true);
  };

  const openAddPackageModal = () => {
    setIsAddPackageModalOpen(true);
  };

  const closeAddPackageModal = () => {
    setIsAddPackageModalOpen(false);
  };

  const pageCount = Math.ceil(totalPackages / itemsPerPage);

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow relative">
        <div className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col">
          <h1 className="text-2xl font-semibold mb-6">{t.title}</h1>
          <div
            className={`flex ${
              isSmallScreen || isMediumScreen
                ? "flex-col items-center space-y-4 w-full"
                : "justify-between items-center"
            } mb-6`}
          >
            <div
              className={`flex ${
                isSmallScreen || isMediumScreen
                  ? "flex-col space-y-3 items-center w-full"
                  : "space-x-3"
              }`}
            >
              <select
                className="px-4 py-3 border border-gray-300 rounded-lg w-full sm:w-64 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                value={categoryFilter}
                onChange={handleCategoryChange}
              >
                <option value="">{t.category}</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id.toString()}>
                    {category.name}
                  </option>
                ))}
              </select>
              <select
                className="px-4 py-3 border border-gray-300 rounded-lg w-full sm:w-64 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                value={subcategoryFilter}
                onChange={handleSubcategoryChange}
                disabled={!categoryFilter}
              >
                <option value="">{t.subcategory}</option>
                {subCategories.map((subCategory) => (
                  <option key={subCategory.id} value={subCategory.id}>
                    {subCategory.name}
                  </option>
                ))}
              </select>
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  placeholder={ml.search}
                  className="px-4 py-3 border border-gray-300 rounded-lg w-full pr-12 outline-none dark:bg-darkBackground dark:border-gray-600 focus:border-purple focus:ring-1 focus:ring-purple transition-colors shadow-sm"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <SearchIcon className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              </div>
            </div>
            <button
              className={`bg-purple text-white ${
                isSmallScreen || isMediumScreen
                  ? "px-4 py-2.5 w-full"
                  : "px-6 py-2.5 w-48"
              } rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all`}
              onClick={openAddPackageModal}
            >
              {t.addPackage}
            </button>
          </div>
          {packages.length > 0 && (
            <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <table className="min-w-full">
                <thead className="bg-gray-50 dark:bg-darkSecondaryBackground border-b border-gray-200 dark:border-gray-700">
                  <tr>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.name}
                    </th>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.category}
                    </th>
                    <th className="py-4 px-6 font-semibold text-left text-sm text-gray-700 dark:text-gray-300">
                      {t.subcategory}
                    </th>
                    <th className="py-4 px-6 font-semibold text-right text-sm text-gray-700 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-darkSecondaryBackground divide-y divide-gray-200 dark:divide-gray-700">
                  {packages.map((pkg) => (
                    <tr
                      key={pkg.id}
                      className="group hover:bg-purple hover:bg-opacity-5 dark:hover:bg-purple dark:hover:bg-opacity-10 transition-all duration-200 cursor-pointer"
                    >
                      <td className="py-4 px-6 text-left text-gray-900 dark:text-gray-100 font-medium">
                        {getLabel(pkg.label, language)}
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600 dark:text-gray-400">
                        {pkg.category}
                      </td>
                      <td className="py-4 px-6 text-left text-gray-600 dark:text-gray-400">
                        {pkg.subCategory}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <div className="flex justify-end items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/add-package/${pkg.id}`);
                            }}
                            className="p-2 hover:bg-blue-50 dark:hover:bg-blue-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/edit"
                            title="Edit package"
                          >
                            <Pencil
                              className="text-gray-400 dark:text-gray-500 group-hover/edit:text-blue-600 dark:group-hover/edit:text-blue-400 group-hover/edit:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openConfirmationModal(pkg);
                            }}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900 dark:hover:bg-opacity-20 rounded-lg transition-all duration-200 group/delete"
                            title="Delete package"
                          >
                            <Trash2
                              className="text-gray-400 dark:text-gray-500 group-hover/delete:text-red-500 dark:group-hover/delete:text-red-400 group-hover/delete:scale-110 transition-all"
                              size={18}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {packages.length === 0 && (
            <div className="text-center py-12 bg-white dark:bg-darkSecondaryBackground rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-2">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                />
              </svg>
              <p className="mt-4 text-lg font-medium text-gray-500 dark:text-gray-400">
                No packages found
              </p>
              <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
                Try adjusting your filters or add a new package
              </p>
            </div>
          )}
          <div className="flex justify-between items-center pt-6 w-full">
            <CustomPagination
              page={page}
              pageCount={pageCount}
              onPageChange={handlePageChange}
            />
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              handleItemsPerPageChange={handleItemsPerPageChange}
            />
          </div>
        </div>
        {loading && (
          <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
            <CircularProgress />
          </div>
        )}
      </div>
      {packageToDelete && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          onClose={() => setIsConfirmationModalOpen(false)}
          onConfirm={() => handleDeletePackage(packageToDelete.id)}
          label={t.deleteConfirmation.replace("{label}", packageToDelete.label)}
        />
      )}
      {isAddPackageModalOpen && (
        <AddPackageModal
          isOpen={isAddPackageModalOpen}
          onClose={closeAddPackageModal}
          translations={gt}
          values={values}
          setValues={setValues}
          onClick={() => navigate("/add-package")}
        />
      )}
      <ToastContainer />
    </div>
  );
};

export default PackagesList;
